import React from 'react';
import {Button, Form, FormControl, Modal,} from 'react-bootstrap';
import {DatePicker, DotsSettings, DropZone, LoaderBtn, renderRow, UploadedDocument,} from 'components';
import {IExpertCertificate} from 'components/models';
import { DocumentType } from 'components/enums';
import {IApiError} from '_helpers/ApiConnector/types';
import styles from './CertificateModal.module.scss';
import moment from 'moment';
import cn from 'classnames';


const CertificateModal = ({
    onHide, onSubmit, title, show, certificate, apiError, onSuccess, onDelete, deleteButtonText, middleText,
}: IProps) => {
    const initialState: IState = {
        regNumber: '',
        issueDate: undefined,
        expireDate: undefined,
        file: undefined,
        regNumError: null,
    };

    const [file, setFile] = React.useState(initialState.file);
    const [regNumber, setRegNumber] = React.useState(initialState.regNumber);
    const [issueDate, setIssueDate] = React.useState(initialState.issueDate);
    const [expireDate, setExpireDate] = React.useState(initialState.expireDate);
    const [processing, setProcessing] = React.useState(false);
    const [validRegNumber, setValidRegNumber] = React.useState(true);
    const [validIssueDate, setValidIssueDate] = React.useState(true);
    const [validExpireDate, setValidExpireDate] = React.useState(true);
    const [regNumError, setRegNumError] = React.useState(initialState.regNumError);

    const [loadingFile, setLoadingFile] = React.useState<ILoadingFile | null>(null);
    const [showDropZone, setShowDropZone] = React.useState(false);

    React.useEffect(() => {
        if (loadingFile && loadingFile.progress !== 100) {
            const timer = setTimeout(() => {
                setLoadingFile({ ...loadingFile, progress: loadingFile.progress + 50 });
            }, 1000);
            return () => clearTimeout(timer);
        }

        if (loadingFile && loadingFile.progress === 100) {
            setLoadingFile(null);
            setShowDropZone(false);
        }

        return undefined;
    }, [loadingFile]);

    React.useEffect(() => {
        if (apiError) {
            setLoadingFile(null);
        }
    }, [apiError]);

    React.useEffect(() => {
        if (apiError && apiError.failures && apiError.failures.regNumber) {
            setRegNumError(apiError.message);
        }
    }, [apiError]);

    React.useEffect(() => {
        if (certificate) {
            setRegNumber(certificate.regNumber);
            setIssueDate(certificate.issueDate);
            setExpireDate(certificate.expireDate);
            setShowDropZone(false);
        } else {
            setRegNumber(initialState.regNumber);
            setIssueDate(initialState.issueDate);
            setExpireDate(initialState.expireDate);
            setShowDropZone(true);
        }

        setLoadingFile(null);
        setProcessing(false);
        setValidRegNumber(true);
        setValidIssueDate(true);
        setValidExpireDate(true);
    }, [certificate, show]);

    const clearState = () => {
        setIssueDate(initialState.issueDate);
        setExpireDate(initialState.expireDate);
        setRegNumber(initialState.regNumber);
        setFile(initialState.file);
        setProcessing(false);
        setValidRegNumber(true);
        setValidIssueDate(true);
        setValidExpireDate(true);
        setLoadingFile(null);
        setShowDropZone(false);
    };

    const doValidate = () => {
        const isValidRegNumber = !!regNumber && !!regNumber.length;
        const isValidIssueDate = !!issueDate;
        const isValidExpireDate = !!expireDate;

        setValidRegNumber(isValidRegNumber);
        setValidIssueDate(isValidIssueDate);
        setValidExpireDate(isValidExpireDate);

        return isValidRegNumber && isValidIssueDate && isValidExpireDate;
    };


    const handleIssueDateChange = (val: string, momentDate: moment.Moment) => {
        setIssueDate(momentDate);
        if (momentDate) {
            setValidIssueDate(true);
        }
    };

    const handleExpireDateChange = (val: string, momentDate: moment.Moment) => {
        setExpireDate(momentDate);
        if (momentDate) {
            setValidExpireDate(true);
        }
    };

    const submit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!doValidate()) return;

        setProcessing(true);
        if (regNumber && issueDate && expireDate) {
            const res = await onSubmit({
                regNumber,
                issueDate,
                expireDate,
                file,
            });

            if (res) {
                clearState();

                if (onSuccess) {
                    onSuccess();
                }

                return;
            }
        }
        setProcessing(false);
    };

    const onHideModal = () => {
        clearState();
        onHide();
    };

    const onDeleteFile = () => {
        setFile(undefined);
        setShowDropZone(true);
    };

    const onDrop = (acceptedFiles: File[]) => {
        setLoadingFile({ file: acceptedFiles[0], progress: 0 });
        setFile(acceptedFiles[0]);
    };

    const renderRegNumber = () => (
        <>
            <FormControl
              type="text"
              name="regNumber"
              value={regNumber}
              onChange={(e: React.FormEvent<any>) => {
                    setRegNumber((e.target as HTMLSelectElement).value);
                    setValidRegNumber(true);
                    setRegNumError(null);
                }}
              isInvalid={!validRegNumber || regNumError !== null}
            />
            {!regNumError && (
                <Form.Control.Feedback type="invalid">
                    Заполните поле
                </Form.Control.Feedback>
            )}
        </>
    );

    const renderIssueDate = () => (
        <>
            <DatePicker
              from={-4}
              to={4}
              onChange={handleIssueDateChange}
              initialDate={certificate ? certificate.issueDate : undefined}
              isInvalid={!validIssueDate ? 'Заполните дату начала' : undefined}
              withoutDetectChanges
            />
        </>
    );

    const renderExpireDate = () => (
        <>
            <DatePicker
              from={-4}
              to={4}
              onChange={handleExpireDateChange}
              initialDate={certificate ? certificate.expireDate : undefined}
              isInvalid={!validExpireDate ? 'Заполните дату окончания' : undefined}
              withoutDetectChanges
            />
        </>
    );

    return (
        <Modal
          show={show}
          onHide={onHideModal}
          dialogClassName={styles.signModal}
          centered
        >
            <form onSubmit={submit}>
                <Modal.Header className={styles.signModal__header} closeButton>
                    <Modal.Title>
                        {title}
                    </Modal.Title>
                    {onDelete ? (
                        <DotsSettings buttons={[{
                            onClick: onDelete,
                            buttonText: deleteButtonText || 'Удалить свидетельство',
                            iconType: 'delete',
                        }]}
                        />
                    ) : null}
                </Modal.Header>
                <Modal.Body className={styles.signModal__body}>
                    {renderRow('НОМЕР', renderRegNumber(), cn(styles.certificateRow, styles.certificateRow_regNumber))}
                    {renderRow('НАЧАЛО', renderIssueDate(), cn(styles.certificateRow, styles.certificateRow_date))}
                    {renderRow('ОКОНЧАНИЕ', renderExpireDate(), cn(styles.certificateRow, styles.certificateRow_date))}

                    {middleText ? <span className="d-block mb-4">{middleText}</span> : null}

                    {showDropZone ? (
                        <DropZone
                          template={{
                                id: '', order: 1, variant: 1, description: '', type: DocumentType.IdentityDoc
                            }}
                          uploadedDocuments={[]}
                          apiError={apiError}
                          loadingFile={loadingFile}
                          onDrop={onDrop}
                          onDeleteFile={onDeleteFile}
                          onDeleteSource={undefined}
                          lastDropTemplateId=""
                        />
                    ) : file || certificate ? (
                        <UploadedDocument
                          file={{
                                fileExtension: file ? file.type : certificate ? certificate.fileExtension : '',
                                fileSize: file ? file.size : certificate ? certificate.fileSize : 0,
                                fileName: file ? file.name : certificate ? 'Скан свидетельства' : '',
                                fileId: '',
                                documentTemplateId: '123',
                            }}
                          onDeleteFile={onDeleteFile}
                        />
                    ) : null}
                </Modal.Body>
                <Modal.Footer className={styles.signModal__footer}>
                    <Form.Control.Feedback type="invalid" className="d-block">
                        {regNumError || (apiError && (!apiError.failures || !Object.keys(apiError.failures).length) ? apiError.message : '')}
                    </Form.Control.Feedback>
                    <LoaderBtn disabled={processing || showDropZone} loading={processing} type="submit">Сохранить</LoaderBtn>
                    <Button onClick={onHideModal} variant="outline-secondary">Отмена</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default CertificateModal;

export interface IProps {
    certificate: IExpertCertificate | null;
    apiError: IApiError | null;
    title: string;
    show: boolean;
    deleteButtonText?: string;
    middleText?: string;
    onHide: () => void;
    onSubmit: (data: ICertificateData) => Promise<boolean>;
    onDelete?: () => void;
    onSuccess?: () => void;
}

export interface ICertificateData {
    id?: string;
    regNumber: string;
    issueDate: moment.Moment;
    expireDate: moment.Moment;
    file?: File;
}

interface IState {
    regNumber?: string;
    issueDate?: moment.Moment;
    expireDate?: moment.Moment;
    file?: File;
    regNumError: string | null;
}

interface ILoadingFile {
    file: File;
    progress: number;
}
